import type { Classroom, ClassroomBody } from '@innovamat/glow-api-client';
import { useGetCurrentWeekPeriodQuery } from '@innovamat/glow-api-client';
import { newId } from '@innovamat/radiance-utils';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../classrooms-management';
import { useClassroomScheduleValues } from '../classrooms-management/hooks/use-classroom-schedule-values';
import { usePutClassroom } from '../classrooms-management/hooks/use-put-classroom';
import { useUser } from '../user-management';

type Props = {
  classroom: Classroom | undefined;
  onClose: () => void;
  onConfigurationChange?: () => void;
  showHourDropdownPractice: boolean;
};

interface UseModifyDigitalPracticeModalReturn {
  configuredDate: {
    digitalPracticeFirstDayOfWeek: string | null | undefined;
    digitalPracticeFirstHourOfWeek: string | null | undefined;
  };
  daysOfWeek: { value: string; label: string; numericDay: number }[];
  hours: { value: string; label: string }[];
  isPending: boolean;
  nextOccurrence: Date | null;
  currentPeriodDate: { date: Date; timezone: string } | null;
  currentWeekPeriod: {
    start: { date: string; timezone: string };
    end: { date: string; timezone: string };
  } | null;
  hasChanges: boolean;
  formatDate: (date: Date, timezone?: string) => string;
  formatTime: (date?: Date, timezone?: string) => string;
  handleOnChangeSelect: (value: string | undefined, key: string) => void;
  handleOnClose: () => void;
  handleOnSave: () => void;
  timezone: string | undefined;
}

interface DayOfWeek {
  value: string;
  label: string;
  numericDay: number;
}

const calculateDaysUntilNextWeekday = (
  currentDay: number,
  targetDay: number,
  currentHour: number,
  targetHour: number
): number => {
  let daysUntilTarget = targetDay - currentDay;
  if (daysUntilTarget === 0 && currentHour < targetHour) {
    daysUntilTarget = 0;
  } else if (daysUntilTarget <= 0) {
    daysUntilTarget += 7;
  }
  return daysUntilTarget;
};

const useModifyDigitalPracticeModal = ({
  classroom,
  onClose,
  onConfigurationChange,
  showHourDropdownPractice,
}: Props): UseModifyDigitalPracticeModalReturn => {
  const { t } = useTranslation();
  const schedule = useClassroomScheduleValues();
  const { user } = useUser();
  const { organization } = useOrganization();
  const queryClient = useQueryClient();

  const [configuredDate, setConfiguredDate] = useState({
    digitalPracticeFirstDayOfWeek: classroom?.digitalPracticeFirstDayOfWeek,
    digitalPracticeFirstHourOfWeek: classroom?.digitalPracticeFirstHourOfWeek,
  });

  const { data: currentWeekPeriod } = useGetCurrentWeekPeriodQuery(
    { classroomId: classroom?.id || '' },
    { enabled: !!classroom?.id }
  );

  const orgTimezone = organization?.timezone ?? 'Europe/Madrid';

  const daysOfWeek: DayOfWeek[] = [
    { value: 'monday', label: t('common.monday'), numericDay: 1 },
    { value: 'tuesday', label: t('common.tuesday'), numericDay: 2 },
    { value: 'wednesday', label: t('common.wednesday'), numericDay: 3 },
    { value: 'thursday', label: t('common.thursday'), numericDay: 4 },
    { value: 'friday', label: t('common.friday'), numericDay: 5 },
    { value: 'saturday', label: t('common.saturday'), numericDay: 6 },
    { value: 'sunday', label: t('common.sunday'), numericDay: 0 },
  ];

  const getNextWeekdayOccurrence = (
    weekday: string | null | undefined,
    hour: string | null | undefined
  ): Date | null => {
    if (!weekday) return null;

    const targetDay = daysOfWeek.find(
      (day) => day.value === weekday
    )?.numericDay;
    if (targetDay === undefined) return null;

    const now = DateTime.now().setZone(orgTimezone);
    const todayDay = now.weekday % 7;
    const currentHour = now.hour;

    const targetHour = hour ? parseInt(hour.split(':')[0], 10) : 0;

    const daysUntilNext = calculateDaysUntilNextWeekday(
      todayDay,
      targetDay,
      currentHour,
      targetHour
    );

    const nextOccurrence = now.plus({ days: daysUntilNext }).set({
      hour: targetHour,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    return nextOccurrence.toJSDate();
  };

  const nextOccurrence = getNextWeekdayOccurrence(
    configuredDate.digitalPracticeFirstDayOfWeek,
    configuredDate.digitalPracticeFirstHourOfWeek
  );

  const hours = Array.from({ length: 24 }, (_, i) => ({
    value: `${i.toString().padStart(2, '0')}:00:00`,
    label: `${i.toString().padStart(2, '0')}:00h`,
  }));

  const handleOnChangeSelect = (
    value: string | undefined,
    key: string
  ): void => {
    setConfiguredDate((prev) => ({ ...prev, [key]: value }));
  };

  const handleOnClose = (): void => {
    setConfiguredDate({
      digitalPracticeFirstDayOfWeek: classroom?.digitalPracticeFirstDayOfWeek,
      digitalPracticeFirstHourOfWeek: classroom?.digitalPracticeFirstHourOfWeek,
    });
    onClose();
  };

  const handleOnSave = (): void => {
    if (isPending) {
      return;
    }

    const body: ClassroomBody = {
      id: classroom?.id || newId(),
      icon: classroom?.icon || 0,
      organizationId: user?.organizationId!,
      level: classroom?.level,
      name: classroom?.name,
      courseId: classroom?.courseId,
      avatar: classroom?.avatar,
      scheduleEnd: classroom?.scheduleEnd,
      scheduleStart: classroom?.scheduleStart,
      ...configuredDate,
    };

    handlePutClassroom(body, schedule.values.timeZone!);
  };

  const { handlePutClassroom, isPending } = usePutClassroom({
    onError: () => {},
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Classroom'] });
      queryClient.invalidateQueries({
        queryKey: ['getCurrentWeekPeriod', { classroomId: classroom?.id }],
      });
      onConfigurationChange?.();
      handleOnClose();
    },
  });

  const formatDate = (date: Date, timezone?: string): string => {
    const dateTime = timezone
      ? DateTime.fromJSDate(date).setZone(timezone)
      : DateTime.fromJSDate(date);

    const weekdayNum = dateTime.weekday % 7;
    const translatedWeekday = daysOfWeek.find(
      (day) => day.numericDay === weekdayNum
    )?.label;
    const dayNumber = dateTime.day;
    return `${translatedWeekday} ${dayNumber}`;
  };

  const formatTime = (date?: Date, timezone?: string): string => {
    if (!showHourDropdownPractice) return '00:00h';
    if (!date) {
      const hour = hours.find(
        (h) => h.value === configuredDate.digitalPracticeFirstHourOfWeek
      )?.label;
      return hour || '00:00h';
    }
    const dateTime = timezone
      ? DateTime.fromJSDate(date).setZone(timezone)
      : DateTime.fromJSDate(date);
    return dateTime.toFormat('HH:mm') + 'h';
  };

  const getCurrentPeriodDate = (): { date: Date; timezone: string } | null => {
    if (currentWeekPeriod?.getCurrentWeekPeriod) {
      const { start } = currentWeekPeriod.getCurrentWeekPeriod;
      return {
        date: DateTime.fromSQL(start.date, { zone: start.timezone }).toJSDate(),
        timezone: start.timezone,
      };
    }
    return null;
  };

  const hasChanges =
    configuredDate.digitalPracticeFirstDayOfWeek !==
      classroom?.digitalPracticeFirstDayOfWeek ||
    configuredDate.digitalPracticeFirstHourOfWeek !==
      classroom?.digitalPracticeFirstHourOfWeek;

  return {
    configuredDate,
    daysOfWeek,
    hours,
    isPending,
    nextOccurrence,
    currentPeriodDate: getCurrentPeriodDate(),
    currentWeekPeriod: currentWeekPeriod?.getCurrentWeekPeriod || null,
    hasChanges,
    formatDate,
    formatTime,
    handleOnChangeSelect,
    handleOnClose,
    handleOnSave,
    timezone: orgTimezone,
  };
};

export default useModifyDigitalPracticeModal;
